import store from "/WebInterface/new-ui/upload/js/reducer.js";
import WSUploads from "/WebInterface/new-ui/upload/js/ws.js"; // This will do WS uploads
import XHRUploads from "/WebInterface/new-ui/upload/js/xhr.js"; // This will do XHR uploads
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import {
  handleFileOpenError,
  invoke,
} from "/WebInterface/new-ui/upload/js/common.js";

let lastUploadNoWriteGrowlAtMs = 0;

class UploadingQueue {
  constructor() {
    this.queue = [];
    this.fileUploadingInProgress = false;
    this.currentUploadingFileInstance = null;
    this.currentFile = null;
    this.completedFilesFromQueue = [];
    this.globalCancelEnabled = false;
    this.globalUploadStartTime = null; // Track when first file started uploading
    this.createdDirectories = new Set(); // Cache of directories already created to avoid redundant makeDir calls
  }

  getQueue = () => {
    return this.queue;
  };

  getCurrentUploadingFileInstance = () => {
    return this.currentUploadingFileInstance;
  };

  getGlobalUploadStartTime = () => {
    return this.globalUploadStartTime;
  };

  isDirectoryCreated = (path) => {
    return this.createdDirectories.has(path);
  };

  markDirectoryCreated = (path) => {
    this.createdDirectories.add(path);
  };

  setQueue = (newQueue) => {
    this.queue = newQueue;
    this.onQueueUpdate(); // Call the method when queue updates
  };

  resetFileProgressState = (file, status = "queued") => {
    if (!file) return file;
    const cleanedFile = {
      ...file,
      status,
      uploadedBytes: 0,
      speed: 0,
      elapsedTime: 0,
      timeRemaining: null,
      overAllSpeed: 0,
      averageUploadingSpeed: null,
      time: null,
      finalizingMsg: "",
      chunkPreparationStalled: false,
      uploadingError: null,
    };
    return cleanedFile;
  };

  onQueueUpdate() {
    const queue = [...this.queue];
    invoke("setUploadingStatus", ["isFilesOnUploadQueue", queue.length !== 0]);
  }

  toggleUploadType = (file) => {
    store.dispatch("updateFileStatusAndRerenderFile", {
      [file.id]: {
        ...file,
        uploadType: file.uploadType === "WS" ? "Normal" : "WS",
      },
    });
  };

  addFileToUploadingQueue = (file) => {
    const privs = invoke("currentDirPrivs");
    if (!privs?.includes("(write)")) {
      const now = Date.now();
      if (now - lastUploadNoWriteGrowlAtMs >= 750) {
        lastUploadNoWriteGrowlAtMs = now;
        invoke("growl", [
          t("youDoNotHavePermissionToUpload"),
          t("uploadNotAllowed"),
          5000,
          true,
        ]);
      }
      return;
    }

    let queue = [...this.queue];
    const isDownloadInProgress = invoke("getDownloadStatus");

    if (isDownloadInProgress) {
      _$("#uploadDeniedInfo").removeClass("is-hidden");
    }

    const queuedFile = this.resetFileProgressState(file, "queued");

    store.dispatch("updateFileStatusAndRerenderFile", {
      [queuedFile.id]: queuedFile,
    });

    queue.push(queuedFile);
    this.setQueue(queue);

    if (queue.length === 1 && !isDownloadInProgress) {
      this.startUploadingCurrentFile(queuedFile);
    }

    store.dispatch("enableGlobalUpload");
  };

  continueUpload() {
    const queue = [...this.queue];
    _$("#uploadDeniedInfo").addClass("is-hidden");

    if (queue.length === 0) {
      return;
    }
    const file = queue[0];
    this.startUploadingCurrentFile(file);
  }

  nextFileInQueue = (canceled) => {
    let queue = [...this.queue];
    const done_file = queue.shift();
    this.completedFilesFromQueue.push(done_file);
    this.setQueue(queue);

    if (this.globalCancelEnabled) {
      this.queue.forEach((file) => {
        const { id: fileId } = file;

        store.dispatch("updateFileStatusAndRerenderFile", {
          [fileId]: {
            ...file,
            status: "canceled",
          },
        });
      });
      queue = [];
      this.setQueue([]);
      this.globalCancel = false;
      // Reset global upload start time when queue is cleared
      this.globalUploadStartTime = null;
    }

    if (queue.length !== 0) {
      const file = queue[0];

      this.startUploadingCurrentFile(file);
      return;
    }

    // Reset global upload start time when queue is empty
    this.globalUploadStartTime = null;
    this.handleUpdateUploadingStatus(false);
    store.dispatch("fileUploadingQueueFinished", {
      canceled,
      completedFiles: this.completedFilesFromQueue,
    });
    store.dispatch("disableGlobalUpload");
    this.completedFilesFromQueue = [];
  };

  stopFileUploadingQueueOnQuotaStorageExceeded = (message) => {
    const queue = [...this.queue];
    clearInterval(this.chunksInfo?.intervalId);

    queue.forEach((file) => {
      handleFileOpenError(file, message || t("failedStorageQuotaExceeded"));
    });

    this.setQueue([]);
    store.dispatch("fileUploadingQueueFinished", {
      canceled: true,
      completedFiles: this.completedFilesFromQueue,
    });
  };

  removeFileFromUploadingQueue = (file) => {
    const id = file?.id;

    if (!id) {
      return;
    }

    let queue = [...this.queue];
    queue = queue.filter(
      (file) => file?.id !== id && this?.currentFile?.id !== id
    );

    this.setQueue(queue);
    store.dispatch("enableGlobalUpload");
  };

  removeFileFromCompletedQueue = (file) => {
    const id = file?.id;

    if (!id) {
      return;
    }

    let completedQueue = [...this.completedFilesFromQueue];
    completedQueue = completedQueue.filter((file) => file?.id !== id);

    this.completedFilesFromQueue = [...completedQueue];
    store.dispatch("enableGlobalUpload");
  };

  handleUpdateUploadingStatus(uploading) {
    if (
      (uploading === true || uploading === false) &&
      uploading !== this.fileUploadingInProgress
    ) {
      this.fileUploadingInProgress = uploading;
      invoke("setUploadingStatus", [
        "isUploading",
        this.fileUploadingInProgress,
      ]);
    }

    return this.fileUploadingInProgress;
  }

  getUploadTypeCustomizations = () => {
    const maxRetries = invoke("getUserCustomizationValue", [
      "maxRetriesBeforeCancellingUpload",
      true,
    ]);

    const maxChunkSize = invoke("getUserCustomizationValue", [
      "upload_MaxChunkSizeInMB",
      true,
    ]);

    const startChunkSizeMB = invoke("getUserCustomizationValue", [
      "upload_StartChunkSizeInMB",
      true,
    ]);

    const altDomains = invoke("getAlternateDomains");

    const serverWriteThresholdWSUpload = invoke("getUserCustomizationValue", [
      "serverWriteThresholdWSUpload",
      true,
    ]);

    const WSUploadMaxRequests = invoke("getUserCustomizationValue", [
      "WSUploadMaxRequests",
      true,
    ]);

    const WSUploadDefaultChunkSize = invoke("getUserCustomizationValue", [
      "WSUploadDefaultChunkSize",
      true,
    ]);

    const showServerWriteProgress = invoke("getUserCustomizationValue", [
      "showServerWriteProgress",
    ]);

    const WSUploadChunkTimeout = invoke("getUserCustomizationValue", [
      "WSUploadChunkTimeout",
      true,
    ]);

    // Log customization values for debugging (including start chunk size)
    console.log("Upload: Customization values", {
      maxRetries,
      maxChunkSize,
      startChunkSizeMB,
      serverWriteThresholdWSUpload,
      WSUploadMaxRequests,
      WSUploadDefaultChunkSize,
      showServerWriteProgress,
      WSUploadChunkTimeout,
    });

    return {
      maxRetries,
      maxChunkSize,
      startChunkSizeMB,
      altDomains,
      serverWriteThresholdWSUpload,
      WSUploadMaxRequests,
      WSUploadDefaultChunkSize,
      showServerWriteProgress,
      WSUploadChunkTimeout,
    };
  };

  startUploadingCurrentFile(file) {
    this.currentFile = file;

    // Set global upload start time when first file starts
    if (!this.globalUploadStartTime) {
      this.globalUploadStartTime = Date.now();
    }

    const { uploadType } = file;
    const {
      maxRetries,
      maxChunkSize,
      startChunkSizeMB,
      altDomains,
      serverWriteThresholdWSUpload,
      WSUploadMaxRequests,
      WSUploadDefaultChunkSize,
      showServerWriteProgress,
      WSUploadChunkTimeout,
    } = this.getUploadTypeCustomizations();

    this.currentUploadingFileInstance =
      uploadType === "WS"
        ? new WSUploads({
            maxRetries,
            serverWriteThresholdWSUpload,
            WSUploadMaxRequests,
            WSUploadDefaultChunkSize,
            showServerWriteProgress,
            startChunkSizeMB,
            maxChunkSizeMB: maxChunkSize,
            WSUploadChunkTimeout,
          })
        : new XHRUploads({
            maxRetries,
            maxChunkSize,
            altDomains,
          });

    store.dispatch("scrollToCurrentUploadFile", { file });
    store.dispatch("enableGlobalUpload");
    this.currentUploadingFileInstance.openFileStream(file);
  }

  pausing() {
    this.currentUploadingFileInstance.pausing();
  }

  resume() {
    this.currentUploadingFileInstance.resume();
  }

  cancel(cancelAll = false) {
    this.globalCancelEnabled = cancelAll;

    if (
      this.currentFile.status !== "canceling" ||
      this.currentFile.status !== "canceled"
    ) {
      this.currentUploadingFileInstance.cancel();
    }
  }

  getGlobalUploadedFiles() {
    return [...this.completedFilesFromQueue, ...this.queue];
  }

  getCurrentUploadingFile() {
    return this.currentFile;
  }

  getCompletedFiles() {
    return this.completedFilesFromQueue;
  }

  setElapsedTime(duration = 1) {
    this.elapsedTime = duration;
  }

  setBytesUploaded(bytes = 1) {
    this.bytesUploaded = bytes;
  }

  setTotalBytesToUpload(bytes = 1) {
    this.totalBytesToUpload = bytes;
  }

  getElapsedTime() {
    return this.elapsedTime;
  }

  getBytesUploaded() {
    return this.bytesUploaded;
  }

  getTotalBytesToUpload() {
    return this.totalBytesToUpload;
  }
}

const uploadingQueue = new UploadingQueue();
export default uploadingQueue;
